import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Empty, Stat, StageChip } from "../../dashboard/primitives";
import { useAuth } from "../../auth/AuthContext";
import {
  subscribeAccountProjects,
  subscribeMilestones,
  subscribeMessages,
} from "../../projects/api";
import { subscribeAccountRequests } from "../../requests/api";
import { openCustomerPortal } from "../../lib/billing";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

const toMs = (ts) =>
  ts?.toDate ? ts.toDate().getTime() : ts ? new Date(ts).getTime() : 0;

const fmtWhen = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Subscribe to every project's milestones and merge into { [projectId]: [...] }.
const useAllMilestones = (projects) => {
  const [byProject, setByProject] = useState({});
  const key = projects.map((p) => p.id).join(",");
  useEffect(() => {
    setByProject({});
    const unsubs = projects.map((p) =>
      subscribeMilestones(p.id, (ms) =>
        setByProject((prev) => ({ ...prev, [p.id]: ms }))
      )
    );
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return byProject;
};

// Merge the latest messages across all projects.
const useRecentMessages = (projects, limit = 4) => {
  const [byProject, setByProject] = useState({});
  const key = projects.map((p) => p.id).join(",");
  useEffect(() => {
    setByProject({});
    const unsubs = projects.map((p) =>
      subscribeMessages(p.id, (msgs) =>
        setByProject((prev) => ({
          ...prev,
          [p.id]: (msgs || []).map((m) => ({ ...m, projectTitle: p.title })),
        }))
      )
    );
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return useMemo(
    () =>
      Object.values(byProject)
        .flat()
        .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
        .slice(0, limit),
    [byProject, limit]
  );
};

const ProgressBar = ({ done, total }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          background: "var(--fw-line)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: "var(--fw-red)",
            transition: "width .3s ease",
          }}
        />
      </div>
      <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
        {done}/{total}
      </span>
    </div>
  );
};

const HomeSection = () => {
  const { user, profile, isAccountOwner } = useAuth();
  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalErr, setPortalErr] = useState(null);

  useEffect(() => {
    if (!profile?.accountId) return;
    return subscribeAccountProjects(profile.accountId, setProjects);
  }, [profile?.accountId]);

  useEffect(() => {
    if (!profile?.accountId) return;
    return subscribeAccountRequests(profile.accountId, setRequests);
  }, [profile?.accountId]);

  const milestonesByProject = useAllMilestones(projects);
  const recentMessages = useRecentMessages(projects);

  const allMilestones = useMemo(
    () => Object.values(milestonesByProject).flat(),
    [milestonesByProject]
  );
  const paidTotal = allMilestones
    .filter((m) => m.status === "paid")
    .reduce((n, m) => n + (m.amount || 0), 0);
  const outstanding =
    allMilestones.reduce((n, m) => n + (m.amount || 0), 0) - paidTotal;
  const doneMilestones = allMilestones.filter((m) => m.status === "paid").length;

  const activeProjects = projects.filter((p) => p.status !== "launched");
  const openRequests = requests.filter(
    (r) => !["accepted", "closed", "rejected", "archived"].includes((r.status || "").toLowerCase())
  );

  const manageBilling = async () => {
    setPortalBusy(true);
    setPortalErr(null);
    try {
      await openCustomerPortal({ user, profile });
    } catch (err) {
      setPortalErr(err?.response?.data?.error || err.message);
      setPortalBusy(false);
    }
  };

  const firstName = (profile?.displayName || user?.email || "").split(" ")[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card title={`Welcome back, ${firstName}`}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 16,
          }}
        >
          <Stat label="Active projects" value={activeProjects.length} />
          <Stat label="Milestones complete" value={`${doneMilestones}/${allMilestones.length}`} />
          <Stat label="Paid to date" value={fmt(paidTotal)} />
          <Stat label="Outstanding" value={fmt(outstanding)} />
          <Stat label="Open requests" value={openRequests.length} />
        </div>
      </Card>

      {!profile?.onboardingDone && (
        <Card title="Finish setting up">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "var(--fw-muted)" }}>
              Tell us about your project so we can hit the ground running.
            </span>
            <Link to="/start" className="fw-btn fw-btn--primary" style={{ padding: "8px 14px", fontSize: 12 }}>
              Complete onboarding →
            </Link>
          </div>
        </Card>
      )}

      <div className="fw-stack-md" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
        <Card
          title="Project status"
          action={
            <Link to="/dashboard/projects" style={{ fontFamily: "var(--fw-mono)", fontSize: 11 }}>
              All projects →
            </Link>
          }
        >
          {projects.length === 0 ? (
            <Empty>
              No active projects —{" "}
              <Link to="/start">start a request</Link> and we'll take it from there.
            </Empty>
          ) : (
            projects.map((p) => {
              const ms = milestonesByProject[p.id] || [];
              const done = ms.filter((m) => m.status === "paid").length;
              const next = ms.find((m) => m.status !== "paid");
              return (
                <div
                  key={p.id}
                  style={{ padding: "14px 0", borderBottom: "1px solid var(--fw-line)" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <Link to={`/dashboard/projects/${p.id}/tasks`} style={{ fontWeight: 600, fontSize: 14 }}>
                      {p.title}
                    </Link>
                    <StageChip
                      status={
                        p.status === "launched"
                          ? "paid"
                          : p.status === "on-hold"
                          ? "closed"
                          : "in_progress"
                      }
                    />
                  </div>
                  <ProgressBar done={done} total={ms.length} />
                  {next && (
                    <div style={{ fontSize: 12, color: "var(--fw-muted)", marginTop: 6 }}>
                      Up next: {next.label}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Card
            title="Billing"
            action={
              <Link to="/dashboard/invoices" style={{ fontFamily: "var(--fw-mono)", fontSize: 11 }}>
                Invoices →
              </Link>
            }
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <Stat label="Outstanding" value={fmt(outstanding)} />
              <Stat label="Paid" value={fmt(paidTotal)} />
            </div>
            {isAccountOwner ? (
              <button
                className="fw-btn fw-btn--ghost"
                style={{ width: "100%", padding: "10px 14px", fontSize: 12 }}
                onClick={manageBilling}
                disabled={portalBusy}
              >
                {portalBusy ? "Opening portal…" : "Manage billing & payment methods"}
              </button>
            ) : (
              <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>
                Billing is managed by your account owner.
              </div>
            )}
            {portalErr && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--fw-red)", fontFamily: "var(--fw-mono)" }}>
                {portalErr}
              </div>
            )}
          </Card>

          <Card
            title="Latest messages"
            action={
              <Link to="/dashboard/messages" style={{ fontFamily: "var(--fw-mono)", fontSize: 11 }}>
                Open →
              </Link>
            }
          >
            {recentMessages.length === 0 ? (
              <Empty>No messages yet</Empty>
            ) : (
              recentMessages.map((m) => (
                <div
                  key={m.id}
                  style={{ padding: "8px 0", borderBottom: "1px solid var(--fw-line)" }}
                >
                  <div style={{ fontSize: 13 }}>
                    {(m.body || "").slice(0, 80)}
                    {(m.body || "").length > 80 ? "…" : ""}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--fw-muted)", marginTop: 2, fontFamily: "var(--fw-mono)" }}>
                    {m.author || "studio"} · {m.projectTitle} · {fmtWhen(m.createdAt)}
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>

      <Card
        title="Your requests"
        action={
          <Link to="/start" className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", fontSize: 11 }}>
            + New request
          </Link>
        }
      >
        {requests.length === 0 ? (
          <Empty>No requests yet</Empty>
        ) : (
          requests.slice(0, 5).map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid var(--fw-line)",
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{r.type || "Request"}</div>
                <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>
                  {Array.isArray(r.scope) ? r.scope.join(", ") : r.scope || "—"} · {fmt(r.budgetUsd)}
                </div>
              </div>
              <StageChip status={r.status || "submitted"} />
            </div>
          ))
        )}
      </Card>
    </div>
  );
};

export default HomeSection;
